import { useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import fetch from 'cross-fetch';
import { Field, Formik } from 'formik';
import jwt_decode from 'jwt-decode';
import React, { useEffect, useState } from 'react';
import AppleSignin from 'react-apple-signin-auth';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  TouchableOpacity,
} from 'react-native';

import { InviteEmailVerificationStep } from '@components/Invite/InviteEmailVerificationStep.web';
import { ScopedSignupModal } from '@components/Modals/ScopedSignupModal.web';
import { Box, ShadowBox, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import TextField from '@components/shared/TextField/TextField';
import {
  Invitation,
  InvitationDetailsDocument,
  InvitationDetailsQuery,
  TaskPriority,
} from '@graphql/generated';
import { useGoogleAuth } from '@hooks/useGoogleAuth';
import { scopedRegisterSchema } from '@schemas/scopedRegisterSchema.web';
import { useAuthContext } from '@src/context/authContext';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
import { sendEmailVerificationCode } from '@utils/emailVerification';
import { getApiConfig } from '@utils/getApiConfig';
import { isMobileBrowser } from '@utils/mobileDeepLink';
import { parseRegisterResponse } from '@utils/parseRegisterResponse';
import { RATE_LIMIT_ERROR_KEY } from '@utils/rateLimit';
import { isSocialIdentityError } from '@utils/socialRegistration';
import { AsyncStorage, SecurityStorage } from '@utils/storage';

type TabKey =
  | 'tasks'
  | 'checklist'
  | 'filesAndMedia'
  | 'activityLog'
  | 'members';

const TABS: { key: TabKey; icon: string }[] = [
  { key: 'tasks', icon: 'Hash' },
  { key: 'checklist', icon: 'FileText' },
  { key: 'filesAndMedia', icon: 'FilesImages' },
  { key: 'activityLog', icon: 'Activity1' },
  { key: 'members', icon: 'Users' },
];

type ScopedRegisterValues = {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
};

export const InvitationPreview: React.FC = () => {
  const navigation = useNavigation();
  const { t } = useTranslation('models');
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [isRoleTooltipVisible, setIsRoleTooltipVisible] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isHoveredGoogle, setIsHoveredGoogle] = useState(false);
  const [isHoveredApple, setIsHoveredApple] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [hasTouchedPassword, setHasTouchedPassword] = useState(false);
  const [isHoveredTerms, setIsHoveredTerms] = useState(false);
  const [isHoveredPrivacyPolicy, setIsHoveredPrivacyPolicy] = useState(false);
  const [mobileStep, setMobileStep] = useState<'select' | 'email-form'>(
    'select'
  );
  // TA-11025: see ScopedSignupModal.web — form values while the OTP step is
  // up, and what to restore if registration sends the user back to the form.
  const [pendingEmailSignup, setPendingEmailSignup] =
    useState<ScopedRegisterValues | null>(null);
  const [restoredForm, setRestoredForm] = useState<{
    values: ScopedRegisterValues;
    errors: Partial<Record<keyof ScopedRegisterValues, string>>;
  } | null>(null);

  const token = window.sessionStorage.getItem('invitation_token') ?? '';
  const rawType = window.sessionStorage.getItem('link_type') ?? '';
  const rawEntityId = window.sessionStorage.getItem('task_or_project_id');
  const entityId =
    rawEntityId && rawEntityId.trim() ? rawEntityId.trim() : undefined;

  const isTeamInvite = rawType === 'team';
  const isProjectInvite = rawType === 'project';
  const isTaskInvite = rawType === 'task';

  const getInvitationType = (type: string): Invitation => {
    if (type === 'project') return Invitation.Project;
    if (type === 'task') return Invitation.Task;
    return Invitation.Team;
  };
  const invitationType = getInvitationType(rawType);

  const { setAuthToken } = useAuthContext();
  const { prompt, userInfo, setUserDetail, clearUserInfo } = useGoogleAuth();
  const apiConfig = getApiConfig();

  const { data, loading, error } = useQuery<InvitationDetailsQuery>(
    InvitationDetailsDocument,
    {
      variables: { token, type: invitationType, entityId },
      skip: !token,
    }
  );

  useEffect(() => {
    if (loading) return;

    const shouldRedirect =
      !token ||
      !!error ||
      (isProjectInvite && data && !data.invitationDetails?.project) ||
      (isTeamInvite && data && !data.invitationDetails?.team) ||
      (isTaskInvite && data && !data.invitationDetails?.task);

    if (shouldRedirect) {
      window.sessionStorage.removeItem('invitation_token');
      window.sessionStorage.removeItem('link_type');
      window.sessionStorage.removeItem('task_or_project_id');
      (navigation as any).navigate('register', { routeName: 'email' });
    }
  }, [
    loading,
    data,
    error,
    navigation,
    token,
    isProjectInvite,
    isTeamInvite,
    isTaskInvite,
  ]);

  useEffect(() => {
    clearUserInfo?.();
  }, []);

  // Persist task invite metadata so TaskWelcomeScreen can render the full task
  // overview card without re-fetching invitationDetails. chat_id is stored for
  // future use (not yet consumed by TaskWelcomeScreen).
  useEffect(() => {
    if (!isTaskInvite || loading) return;
    const details = data?.invitationDetails;
    if (!details?.task) return;
    window.sessionStorage.setItem('task_invite_name', details.task.name ?? '');
    window.sessionStorage.setItem(
      'task_invite_inviter_name',
      details.invitedByName ?? ''
    );
    window.sessionStorage.setItem(
      'task_invite_users_count',
      String(details.task.usersCount ?? 0)
    );
    if (details.chat?.id) {
      window.sessionStorage.setItem('task_invite_chat_id', details.chat.id);
    }
  }, [isTaskInvite, loading, data]);

  const details = data?.invitationDetails;
  const project = details?.project;
  const team = details?.team;
  const task = details?.task;
  const isLinkInvite = details?.isLinkInvite ?? false;
  const invitationEmail = details?.invitationEmail ?? '';
  const invitationRole = details?.invitationRole ?? 'Member';
  const invitedByName = details?.invitedByName ?? '';

  const formattedRole =
    invitationRole.charAt(0).toUpperCase() + invitationRole.slice(1);
  const isAdminRole = invitationRole.toLowerCase() === 'admin';
  const handleOAuthSubmit = async (oauthUser: {
    email: string;
    given_name?: string;
    family_name?: string;
    id: string;
    provider: string;
    identity_token?: string;
  }) => {
    setSubmitError(null);
    const socialSignInExpired = t('onBoarding.error.socialSignInExpired', {
      provider: oauthUser.provider,
    });
    // TA-11025: the backend rejects social registration without a live,
    // verifiable identity token — don't send one that is bound to fail.
    if (!oauthUser.identity_token) {
      clearUserInfo?.();
      setSubmitError(socialSignInExpired);
      return;
    }
    const isEmailMatch =
      isLinkInvite ||
      oauthUser.email.toLowerCase() === invitationEmail.toLowerCase();

    const registerPayload = {
      user: {
        email: oauthUser.email.toLowerCase(),
        password: '',
        terms_of_service: true,
        name: [oauthUser.given_name, oauthUser.family_name]
          .filter(Boolean)
          .join(' '),
        phone_number: '',
        provider: oauthUser.provider,
        uid: oauthUser.id,
        identity_token: oauthUser.identity_token,
        ...(isEmailMatch && { invitation_token: token }),
        ...(isLinkInvite && entityId && { entity_id: entityId }),
      },
    };

    try {
      const response = await fetch(`${apiConfig.apiUrl}/api/v3/users.json`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerPayload),
      });
      const responseData = await response.json().catch(() => null);
      const result = parseRegisterResponse(response.ok, responseData, {
        status: response.status,
        rateLimitMessage: t(RATE_LIMIT_ERROR_KEY),
      });

      if (isSocialIdentityError(result)) {
        // The token expired or didn't verify. Drop this sign-in so the
        // provider buttons start a fresh one instead of resending it.
        clearUserInfo?.();
        setSubmitError(socialSignInExpired);
      } else if (result.status === 'error') {
        setSubmitError(result.message ?? t('onBoarding.networkError'));
      } else {
        const authToken = result.authToken;
        Sentry.setUser({ email: oauthUser.email });
        window.sessionStorage.setItem('setKeepSignedIn', '1');
        if (isEmailMatch) {
          window.sessionStorage.setItem('goto_welcome', '0');
          window.sessionStorage.setItem('invite_signup', '1');
          window.sessionStorage.setItem('invite_signup_type', 'team');
          if (isLinkInvite) {
            window.sessionStorage.setItem('is_link_invite', '1');
          }
        } else {
          // TA-10886: the trial welcome modal was retired — a signup whose
          // e-mail does not match the invite is a plain signup
          window.sessionStorage.setItem('goto_welcome', '0');
          window.sessionStorage.removeItem('invitation_token');
          window.sessionStorage.removeItem('link_type');
          window.sessionStorage.removeItem('task_or_project_id');
        }
        await SecurityStorage.setItem('authToken', authToken);
        clearUserInfo?.();
        setAuthToken(authToken);
      }
    } catch {
      setSubmitError(t('onBoarding.networkError'));
    }
  };

  useEffect(() => {
    if (userInfo) {
      handleOAuthSubmit(userInfo);
    }
  }, [userInfo]);

  const handleAppleSuccess = async (response: any) => {
    try {
      const localResponse = await AsyncStorage.getItem('appleUserInfo');
      const userData = localResponse ? JSON.parse(localResponse) : {};

      // TA-11025: only this sign-in's token is proof the backend can verify.
      // The copy cached in AsyncStorage is from an earlier sign-in and long
      // expired, so it is never a fallback — ask for a fresh sign-in instead.
      const idToken: string | undefined = response?.authorization?.id_token;
      if (!idToken) {
        setSubmitError(
          t('onBoarding.error.socialSignInExpired', { provider: 'Apple' })
        );
        return;
      }

      const decoded = jwt_decode<{ sub?: string; email?: string }>(idToken);
      const decodeSub = decoded?.sub ?? '';
      const decodeEmail = decoded?.email ?? '';

      if (!decodeSub) {
        setSubmitError(t('onBoarding.error.appleSignInFailed'));
        return;
      }

      const userInfoPayload = {
        id: decodeSub,
        // The backend requires the registration email to equal the token's
        // (possibly private-relay) email, so the claim wins over any cached
        // or popup-supplied address.
        email: decodeEmail || response?.user?.email || userData?.user?.email,
        given_name:
          response?.user?.name?.firstName || userData?.user?.name?.firstName,
        family_name:
          response?.user?.name?.lastName || userData?.user?.name?.lastName,
        provider: 'Apple',
        identity_token: idToken,
      };

      if (response.user?.email) {
        await AsyncStorage.setItem('appleUserInfo', JSON.stringify(response));
      }
      setUserDetail?.(userInfoPayload);
    } catch {
      setSubmitError(t('onBoarding.networkError'));
    }
  };

  // TA-11025 (pairs with TA-11024): v3 email/password registration must prove
  // email ownership. Submitting the form only sends the code; registration
  // happens from the code step with `email_verification_code` attached.
  const handleFormSubmit = async (values: ScopedRegisterValues) => {
    setSubmitError(null);
    try {
      const sent = await sendEmailVerificationCode(values.email);
      if (!sent.ok) {
        setSubmitError(
          sent.error ?? t('registerVerifyEmail.unableToSendVerificationCode')
        );
        return;
      }
    } catch {
      setSubmitError(t('onBoarding.networkError'));
      return;
    }
    setRestoredForm(null);
    setPendingEmailSignup(values);
  };

  const backToEmailForm = (
    values: ScopedRegisterValues,
    errors: Partial<Record<keyof ScopedRegisterValues, string>> = {}
  ) => {
    setRestoredForm({ values, errors });
    setPendingEmailSignup(null);
  };

  const registerWithEmail = async (
    values: ScopedRegisterValues,
    emailVerificationCode: string
  ): Promise<string | null> => {
    const registerUrl = `/api/v3/users.json`;
    const registerPayload = {
      user: {
        email: values.email.toLowerCase(),
        password: values.password,
        terms_of_service: true,
        name: [values.first_name, values.last_name]
          .filter((item) => item !== '')
          .join(' '),
        phone_number: '',
        invitation_token: token,
        email_verification_code: emailVerificationCode,
        ...(isLinkInvite && entityId && { entity_id: entityId }),
      },
    };
    const authEndpoint = `${apiConfig.apiUrl}${registerUrl}`;

    try {
      const response = await fetch(authEndpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerPayload),
      });
      const responseData = await response.json().catch(() => null);
      const result = parseRegisterResponse(response.ok, responseData, {
        status: response.status,
        rateLimitMessage: t(RATE_LIMIT_ERROR_KEY),
      });

      if (result.status === 'error') {
        // A bad/expired code stays on the code step; anything about the
        // email itself belongs on the form the user filled in.
        if (result.fieldErrors.email_verification_code) {
          return result.fieldErrors.email_verification_code;
        }
        if (result.message === 'Email has already been taken') {
          backToEmailForm(values, { email: t('onBoarding.emailAlreadyInUse') });
          setSubmitError(t('onBoarding.emailAlreadyInUse'));
          return null;
        }
        if (result.fieldErrors.email) {
          backToEmailForm(values, { email: result.fieldErrors.email });
          setSubmitError(result.fieldErrors.email);
          return null;
        }
        return result.message ?? t('onBoarding.networkError');
      } else {
        const authToken = result.authToken;
        Sentry.setUser({ email: values.email });
        window.sessionStorage.setItem('setKeepSignedIn', '1');
        window.sessionStorage.setItem('goto_welcome', '0');
        window.sessionStorage.setItem('invite_signup', '1');
        window.sessionStorage.setItem('invite_signup_type', 'team');
        if (isLinkInvite) {
          window.sessionStorage.setItem('is_link_invite', '1');
        }
        await SecurityStorage.setItem('authToken', authToken);
        setAuthToken(authToken);
        return null;
      }
    } catch {
      return t('onBoarding.networkError');
    }
  };

  const emailVerificationContent = pendingEmailSignup && (
    <InviteEmailVerificationStep
      email={pendingEmailSignup.email}
      onSubmitCode={(code) => registerWithEmail(pendingEmailSignup, code)}
      onBack={() => backToEmailForm(pendingEmailSignup)}
    />
  );

  if (loading) {
    return (
      <Box flex={1} alignItems='center' justifyContent='center'>
        <ActivityIndicator />
      </Box>
    );
  }

  // Team invite layout — two-branch: mobile (two-step) / web (inline card)
  if (isTeamInvite && team) {
    const isMobile = isMobileBrowser();

    const ADMIN_CAPABILITIES = [
      t('onBoarding.teamAdminCapabilities.manageProjectAndTask'),
      t('onBoarding.teamAdminCapabilities.inviteRemoveMembers'),
      t('onBoarding.teamAdminCapabilities.manageRolesPermissions'),
      t('onBoarding.teamAdminCapabilities.configureTeamSettings'),
    ];

    const initialValues: ScopedRegisterValues = {
      email: isLinkInvite ? '' : invitationEmail,
      first_name: '',
      last_name: '',
      password: '',
    };

    // Shared team context banner — tooltip uses press on mobile, hover on web
    const contextBanner = (
      <Box
        backgroundColor='greenSecondaryMild'
        borderRadius='s'
        p='m'
        mb='l'
        style={{ zIndex: 10, overflow: 'visible' } as any}>
        <Text variant='labelEmphasized' color='textPrimary' mb='xxs'>
          {team.name}
        </Text>
        <Box flexDirection='row' alignItems='center' flexWrap='wrap'>
          <Text variant='webBodySecondary' color='textSecondary'>
            {t('onBoarding.youllJoinAsPrefix')}
          </Text>
          {isAdminRole ? (
            <Pressable
              onPress={
                isMobile ? () => setIsRoleTooltipVisible((v) => !v) : undefined
              }
              {...(!isMobile &&
                ({
                  onMouseEnter: () => setIsRoleTooltipVisible(true),
                  onMouseLeave: () => setIsRoleTooltipVisible(false),
                } as any))}
              style={
                {
                  position: 'relative',
                  zIndex: 9999,
                  overflow: 'visible',
                  cursor: isMobile ? 'default' : 'pointer',
                } as any
              }>
              <Box
                borderBottomWidth={1}
                borderColor='textPrimary'
                style={{ borderStyle: 'dotted' } as any}>
                <Text variant='bodySecondaryBold' color='textPrimary'>
                  {formattedRole}
                </Text>
              </Box>
              {isRoleTooltipVisible && (
                <Box
                  position='absolute'
                  style={
                    {
                      top: theme.spacing.l,
                      left: 0,
                      backgroundColor: theme.colors.textPrimary,
                      borderRadius: theme.borderRadii.xs,
                      paddingHorizontal: theme.spacing.s,
                      paddingVertical: theme.spacing.xs,
                      zIndex: 9999,
                      minWidth: 200,
                    } as any
                  }>
                  {ADMIN_CAPABILITIES.map((item, i) => (
                    <Text key={i} variant='metadata' color='white'>
                      {'\u2022 '}
                      {item}
                    </Text>
                  ))}
                </Box>
              )}
            </Pressable>
          ) : (
            <Text variant='bodySecondaryBold' color='textPrimary'>
              {formattedRole}
            </Text>
          )}
          <Text variant='webBodySecondary' color='textSecondary'>
            {t('onBoarding.youllJoinAsSuffix', {
              count: team.totalMembers,
            })}
          </Text>
        </Box>
      </Box>
    );

    // Shared inviter subtitle
    const inviterSubtitle = (
      <Box flexDirection='row' justifyContent='center' flexWrap='wrap' mb='l'>
        {!isLinkInvite && !!invitedByName && (
          <>
            <Text variant='metadataSemiBold' color='textPrimary'>
              {invitedByName}
            </Text>
            <Text variant='metadata' color='textSecondary'>
              {t('onBoarding.invitedYou')}
            </Text>
          </>
        )}
      </Box>
    );

    // Shared Google + Apple buttons
    const socialButtons = (
      <Box flexDirection='row' mb='m'>
        <Box
          flex={1}
          mr='xs'
          borderWidth={1}
          borderColor='grey02'
          borderRadius='xs'
          style={{ overflow: 'hidden' } as any}>
          <Button
            onMouseEnter={() => setIsHoveredGoogle(true)}
            onMouseLeave={() => setIsHoveredGoogle(false)}
            onPress={() => prompt()}
            prefix={
              <Icon
                variant='l'
                marginRight='xs'
                name='Google'
                color='textPrimary'
                accessibilityLabel='googleIcon'
              />
            }
            backgroundColor={isHoveredGoogle ? 'grey01' : 'white'}
            accessibilityLabel={t('onBoarding.google')}
            fullWidth
            isSmall>
            <Text variant='webBodySecondary'>{t('onBoarding.google')}</Text>
          </Button>
        </Box>
        <Box
          flex={1}
          borderWidth={1}
          borderColor='grey02'
          borderRadius='xs'
          style={{ overflow: 'hidden' } as any}>
          <AppleSignin
            authOptions={{
              clientId: process.env.APPLE_CLIENT_ID_WEB,
              scope: 'name email',
              redirectURI: process.env.APPLE_REDIRECT_UI,
              state: '',
              nonce: 'nonce',
              usePopup: true,
            }}
            render={(props: any) => (
              <Button
                onMouseEnter={() => setIsHoveredApple(true)}
                onMouseLeave={() => setIsHoveredApple(false)}
                fullWidth
                isSmall
                backgroundColor={isHoveredApple ? 'grey01' : 'white'}
                accessibilityLabel={t('onBoarding.apple')}
                prefix={
                  <Icon
                    variant='l'
                    name='Apple24'
                    color='textPrimary'
                    accessibilityLabel={t('onBoarding.apple')}
                  />
                }
                {...props}>
                <Text variant='webBodySecondary'>{t('onBoarding.apple')}</Text>
              </Button>
            )}
            uiType='light'
            className='apple-auth-btn'
            onSuccess={(response: any) => handleAppleSuccess(response)}
            onError={(error: any) => {
              Sentry.captureException(error);
              setSubmitError(t('onBoarding.networkError'));
            }}
          />
        </Box>
      </Box>
    );

    // Shared email form (Formik + all fields + submit button)
    const formFields = (
      <Formik
        initialValues={restoredForm?.values ?? initialValues}
        initialErrors={restoredForm?.errors}
        initialTouched={restoredForm ? { email: true } : undefined}
        validationSchema={scopedRegisterSchema}
        validateOnMount={false}
        onSubmit={(values) => handleFormSubmit(values)}>
        {({ handleSubmit: formikSubmit, isSubmitting, values }) => {
          const validLength = values.password.length >= 8;
          const validNumber = /\d/.test(values.password);
          const validUppercase = /[A-Z]/.test(values.password);
          const validSpecial = /[^A-Za-z0-9]/.test(values.password);
          const isFormValid = scopedRegisterSchema.isValidSync(values);

          return (
            <Box>
              <Box mb='l'>
                <Field
                  component={TextField}
                  label={t('shared:email')}
                  name='email'
                  marginBottom='none'
                  placeholder='johndoe@tasktag.com'
                  editable={isLinkInvite}
                  {...(!isLinkInvite && {
                    customBackgroundColor: theme.colors.grey02,
                    customBorderColor: theme.colors.grey03,
                  })}
                  textInputProps={{
                    autoCapitalize: 'none',
                    autoCorrect: false,
                    keyboardType: 'email-address',
                    editable: isLinkInvite,
                    ...(!isLinkInvite && {
                      style: { color: theme.colors.grey03 },
                    }),
                  }}
                  accessibilityLabel='Email address'
                  validateOnChange={false}
                />
              </Box>

              <Box flexDirection='row' mb='s'>
                <Box flex={1} mr='xs'>
                  <Field
                    component={TextField}
                    label={t('shared:firstName')}
                    name='first_name'
                    marginBottom='none'
                    placeholder='e.g. John'
                    accessibilityLabel='First name'
                    validateOnChange={false}
                  />
                </Box>
                <Box flex={1}>
                  <Field
                    component={TextField}
                    label={t('shared:lastName')}
                    name='last_name'
                    marginBottom='none'
                    placeholder='e.g. Doe'
                    accessibilityLabel='Last name'
                    validateOnChange={false}
                  />
                </Box>
              </Box>

              <Box mb='m'>
                <Field
                  component={TextField}
                  label={t('shared:password')}
                  name='password'
                  marginBottom='none'
                  placeholder={t('onBoarding.enterPassword')}
                  onFocus={() => {
                    setIsPasswordFocused(true);
                    setHasTouchedPassword(true);
                  }}
                  onBlur={() => setIsPasswordFocused(false)}
                  suffix={
                    <Icon
                      name={showPassword ? 'Eye' : 'EyeOff'}
                      variant='l'
                      color='textPrimary'
                      onPress={() => setShowPassword(!showPassword)}
                    />
                  }
                  textInputProps={{
                    autoCapitalize: 'none',
                    autoCorrect: false,
                    secureTextEntry: !showPassword,
                  }}
                  accessibilityLabel='Password'
                  validateOnChange={false}
                />
                {(isPasswordFocused || hasTouchedPassword) && (
                  <Box mt='xs'>
                    <Text variant='metadata' color='textSecondary' mb='xxs'>
                      {t('onBoarding.yourPasswordMustContain')}
                    </Text>
                    {[
                      {
                        label: t('onBoarding.atLeast8Characters'),
                        valid: validLength,
                      },
                      {
                        label: t('onBoarding.atLeast1Number'),
                        valid: validNumber,
                      },
                      {
                        label: t('onBoarding.atLeast1UppcaseLetter'),
                        valid: validUppercase,
                      },
                      {
                        label: t('onBoarding.atLeast1SpecialCharacter'),
                        valid: validSpecial,
                      },
                    ].map((rule, idx) => (
                      <Box
                        key={idx}
                        flexDirection='row'
                        alignItems='center'
                        mb='xxs'>
                        {rule.valid ? (
                          <Icon
                            name='Check'
                            variant='s'
                            color='greenSecondary'
                            marginRight='xxs'
                          />
                        ) : (
                          <Text
                            variant='metadata'
                            color='textSecondary'
                            style={{ marginRight: theme.spacing.xxs }}>
                            {'\u2022'}
                          </Text>
                        )}
                        <Text
                          variant='metadata'
                          color={
                            rule.valid ? 'greenSecondary' : 'textSecondary'
                          }>
                          {rule.label}
                        </Text>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {!!submitError && (
                <Text
                  variant='metadata'
                  color='alertRed'
                  mb='m'
                  textAlign='center'>
                  {submitError}
                </Text>
              )}

              <Box mb='m'>
                <Text
                  variant='metadataSecondaryNew'
                  color='textSecondary'
                  style={{ lineHeight: 16 }}>
                  {t('onBoarding.bySigningUpIAgreeToTheTaskTag')}
                  <Text
                    variant='metadataSecondaryNew'
                    color='greenSecondary'
                    textDecorationLine={
                      isHoveredTerms ? 'underline' : undefined
                    }
                    onMouseEnter={() => setIsHoveredTerms(true)}
                    onMouseLeave={() => setIsHoveredTerms(false)}
                    onPress={async () => {
                      await Linking.openURL(
                        'https://www.tasktag.com/terms-and-conditions'
                      );
                    }}>
                    {t('onBoarding.termsAndConditions')}
                  </Text>
                  {t('onBoarding.and')}
                  <Text
                    variant='metadataSecondaryNew'
                    color='greenSecondary'
                    textDecorationLine={
                      isHoveredPrivacyPolicy ? 'underline' : undefined
                    }
                    onMouseEnter={() => setIsHoveredPrivacyPolicy(true)}
                    onMouseLeave={() => setIsHoveredPrivacyPolicy(false)}
                    onPress={async () => {
                      await Linking.openURL('https://www.tasktag.com/privacy');
                    }}>
                    {t('onBoarding.privacyPolicy')}
                  </Text>
                  {t('onBoarding.dot')}
                </Text>
              </Box>

              <Button
                variant='webprimary'
                fullWidth
                isSmall
                disabled={isSubmitting || !isFormValid}
                onPress={() => formikSubmit()}
                accessibilityLabel={t('onBoarding.joinThis', {
                  entityType: 'Team',
                })}>
                {t('onBoarding.joinThis', { entityType: 'Team' })}
              </Button>
            </Box>
          );
        }}
      </Formik>
    );

    // ── Mobile browser: two-step flow ──
    if (isMobile) {
      return (
        <Box flex={1} backgroundColor='white'>
          {/* Backdrop to dismiss tooltip on mobile */}
          {isRoleTooltipVisible && (
            <Pressable
              style={
                {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 10,
                } as any
              }
              onPress={() => setIsRoleTooltipVisible(false)}
            />
          )}

          {mobileStep === 'select' && (
            <ScrollView
              contentContainerStyle={{
                paddingHorizontal: theme.spacing.m,
                paddingVertical: theme.spacing.l,
                flexGrow: 1,
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <Box width='100%'>
                <Text variant='heading2' textAlign='center' mb='xxs'>
                  {t('onBoarding.createAnAccount')}
                </Text>
                {inviterSubtitle}
                {contextBanner}
                {socialButtons}

                <Box flexDirection='row' alignItems='center' mb='l'>
                  <Box flex={1} height={1} backgroundColor='grey02' />
                  <Text variant='metadata' color='textSecondary' mx='s'>
                    {t('onBoarding.orContinueWithEmail')}
                  </Text>
                  <Box flex={1} height={1} backgroundColor='grey02' />
                </Box>

                <TouchableOpacity
                  onPress={() => setMobileStep('email-form')}
                  accessibilityLabel={t('onBoarding.modal.signUpWithEmail')}>
                  <Box
                    width='100%'
                    alignItems='center'
                    justifyContent='center'
                    borderWidth={1}
                    borderColor='textPrimary'
                    borderRadius='l'
                    style={{ height: theme.spacing.xxl } as any}>
                    <Text variant='labelSmall' color='textPrimary'>
                      {t('onBoarding.modal.signUpWithEmail')}
                    </Text>
                  </Box>
                </TouchableOpacity>

                <Box
                  flexDirection='row'
                  alignItems='center'
                  justifyContent='center'
                  mt='m'>
                  <Text variant='metadata' color='textPrimary'>
                    {t('onBoarding.alreadyHaveAnAccount')}
                  </Text>
                  <Text
                    variant='metadataSemiBold'
                    color='greenSecondary'
                    accessibilityLabel={t('onBoarding.logIn')}
                    onPress={() => (navigation as any).navigate('login')}>
                    {t('onBoarding.logIn')}
                  </Text>
                </Box>
              </Box>
            </ScrollView>
          )}

          {mobileStep === 'email-form' && (
            <ScrollView
              contentContainerStyle={{
                paddingHorizontal: theme.spacing.m,
                paddingVertical: theme.spacing.l,
                flexGrow: 1,
                justifyContent: 'center',
              }}>
              {emailVerificationContent || (
                <>
                  <Box flexDirection='row' alignItems='center' mb='l'>
                    <Pressable
                      onPress={() => setMobileStep('select')}
                      style={
                        {
                          width: 32,
                          height: 32,
                          borderRadius: theme.borderRadii.xs,
                          backgroundColor: theme.colors.grey02,
                          alignItems: 'center',
                          justifyContent: 'center',
                        } as any
                      }>
                      <Icon
                        name='ChevronLeft'
                        variant='m'
                        color='textPrimary'
                      />
                    </Pressable>
                    <Text
                      variant='heading2'
                      textAlign='center'
                      style={{ flex: 1 }}>
                      {t('onBoarding.modal.signUpWithEmail')}
                    </Text>
                    <Box width={32} />
                  </Box>
                  {formFields}
                </>
              )}
            </ScrollView>
          )}
        </Box>
      );
    }

    // ── Web browser: inline card form ──
    return (
      <ScrollView
        style={{ flex: 1, backgroundColor: theme.colors.grey01 }}
        contentContainerStyle={{
          flexGrow: 1,
          alignItems: 'center',
          paddingVertical: theme.spacing.xl,
          paddingHorizontal: theme.spacing.m,
        }}>
        {/* Logo */}
        <Box alignItems='center' mb='l'>
          <Icon
            name='Logo'
            width={isMobile ? 90 : 120}
            height={isMobile ? 27 : 36}
            color='textPrimary'
          />
        </Box>

        {/* Card */}
        <ShadowBox
          variant='base'
          borderRadius='m'
          backgroundColor='white'
          style={{ width: '100%', maxWidth: 560 } as any}>
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ padding: theme.spacing.l }}>
            {/* Heading */}
            <Text variant='heading2' textAlign='center' mb='xxs'>
              {t('onBoarding.createAnAccount')}
            </Text>

            {inviterSubtitle}
            {contextBanner}

            {emailVerificationContent || (
              <>
                {socialButtons}

                <Box flexDirection='row' alignItems='center' mb='m'>
                  <Box flex={1} height={1} backgroundColor='grey02' />
                  <Text variant='metadata' color='textSecondary' mx='s'>
                    {t('onBoarding.orContinueWithEmail')}
                  </Text>
                  <Box flex={1} height={1} backgroundColor='grey02' />
                </Box>

                {formFields}
              </>
            )}

            <Box
              flexDirection='row'
              alignItems='center'
              justifyContent='center'
              mt='m'>
              <Text variant='metadata' color='textPrimary'>
                {t('onBoarding.alreadyHaveAnAccount')}
              </Text>
              <Text
                variant='metadataSemiBold'
                color='greenSecondary'
                accessibilityLabel={t('onBoarding.logIn')}
                onPress={() => (navigation as any).navigate('login')}>
                {t('onBoarding.logIn')}
              </Text>
            </Box>
          </ScrollView>
        </ShadowBox>
      </ScrollView>
    );
  }

  // Task invite layout — preview card with modal signup
  if (isTaskInvite && task) {
    const isMobile = isMobileBrowser();

    const priorityLabel = (() => {
      if (task.priority === TaskPriority.High)
        return t('invitationPreview.task.highPriority');
      if (task.priority === TaskPriority.Medium)
        return t('invitationPreview.task.mediumPriority');
      if (task.priority === TaskPriority.Low)
        return t('invitationPreview.task.lowPriority');
      return undefined;
    })();
    const priorityColor = (() => {
      if (task.priority === TaskPriority.High) return 'alertRed';
      if (task.priority === TaskPriority.Medium) return 'pastelYellow';
      if (task.priority === TaskPriority.Low) return 'blue';
      return undefined;
    })();

    const formatDate = (dateStr?: string | null) => {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    };
    const startDate = formatDate(task.startDate);
    const dueDate = formatDate(task.dueDate);
    const dateRange = [startDate, dueDate].filter(Boolean).join(' - ');

    const ctaLabel = isLinkInvite
      ? t('invitationPreview.task.requestToJoin')
      : t('invitationPreview.task.joinThisTask');

    const projectIconName =
      (task.projectIcon as keyof typeof Images) || 'BuildingHomeHouse';

    const mainContent = (
      <Box width='100%' style={{ maxWidth: isMobile ? 480 : 640 } as any}>
        {/* Heading */}
        <Box mb='m'>
          <Text
            variant={isMobile ? 'heading2' : 'heading3'}
            color='textPrimary'
            textAlign='center'>
            {isLinkInvite
              ? t('invitationPreview.task.youreInvitedToJoinATask')
              : t('invitationPreview.task.youveBeenAssignedATask')}
          </Text>
          {!isLinkInvite && !!invitedByName && (
            <Text
              variant={isMobile ? 'bodySecondaryNew' : 'labelLarge'}
              color='textSecondary'
              textAlign='center'
              style={{ marginTop: 4 } as any}>
              {t('invitationPreview.task.byName', { name: invitedByName })}
            </Text>
          )}
        </Box>

        {/* Task Card */}
        <Box
          backgroundColor='white'
          borderRadius={isMobile ? 'l' : 'm'}
          borderWidth={1}
          borderColor='grey02'
          p='m'
          mb='l'
          style={{ gap: isMobile ? theme.spacing.m : theme.spacing.l } as any}>
          {/* Grey info box */}
          <Box
            backgroundColor='grey01'
            borderRadius={isMobile ? 'm' : 's'}
            p='m'>
            <Text
              variant={isMobile ? 'labelLarge' : 'heading2'}
              color='textPrimary'
              mb='s'>
              {task.name}
            </Text>

            <Box flexDirection='row' alignItems='center' flexWrap='wrap'>
              {!!task.projectName && (
                <Box flexDirection='row' alignItems='center' mr='m'>
                  <Icon
                    name={projectIconName}
                    variant='s'
                    color={isMobile ? 'grey06' : 'greenSecondary'}
                    width={isMobile ? 14 : undefined}
                    height={isMobile ? 14 : undefined}
                    marginRight='xxs'
                  />
                  <Text
                    variant={isMobile ? 'metadata1' : 'labelSmall'}
                    color='textSecondary'>
                    {task.projectName}
                  </Text>
                </Box>
              )}
              {!!invitedByName && (
                <Box flexDirection='row' alignItems='center'>
                  <Box
                    width={isMobile ? 14 : 20}
                    height={isMobile ? 14 : 20}
                    borderRadius='xxl'
                    backgroundColor='greenDark'
                    alignItems='center'
                    justifyContent='center'
                    marginRight='xxs'>
                    <Icon
                      name='User'
                      variant='s'
                      color='white'
                      width={isMobile ? 10 : 14}
                      height={isMobile ? 10 : 14}
                    />
                  </Box>
                  <Text
                    variant={isMobile ? 'metadata1' : 'labelSmall'}
                    color='textSecondary'>
                    {invitedByName}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>

          {/* Priority & Date pills */}
          <Box flexDirection='row' alignItems='center'>
            {!!priorityLabel && (
              <Box
                flexDirection='row'
                alignItems='center'
                backgroundColor='white'
                paddingHorizontal='s'
                height={isMobile ? 28 : 32}
                borderRadius='l'
                borderWidth={1}
                borderColor='grey02'
                mr='xs'>
                <Text
                  variant={isMobile ? 'metadata1' : 'webBodySecondary'}
                  color={priorityColor}>
                  {priorityLabel}
                </Text>
              </Box>
            )}
            {!!dateRange && (
              <Box
                flexDirection='row'
                alignItems='center'
                backgroundColor='white'
                paddingHorizontal='s'
                height={isMobile ? 28 : 32}
                borderRadius='l'
                borderWidth={1}
                borderColor='grey02'>
                <Text
                  variant={isMobile ? 'metadata1' : 'webBodySecondary'}
                  color='textSecondary'>
                  {t('invitationPreview.task.due', { range: dateRange })}
                </Text>
              </Box>
            )}
          </Box>

          {/* Description */}
          {!!task.description && (
            <Box>
              <Text
                variant={isMobile ? 'labelSmall' : 'labelEmphasized'}
                color='textPrimary'
                mb='xxs'>
                {t('invitationPreview.task.description')}
              </Text>
              <Text
                variant={isMobile ? 'metadata1' : 'webBodySecondary'}
                color='textSecondary'
                style={isMobile ? ({ lineHeight: 18 } as any) : undefined}>
                {task.description}
              </Text>
            </Box>
          )}

          {/* Assigned To */}
          <Box>
            <Text
              variant={isMobile ? 'labelSmall' : 'labelEmphasized'}
              color='textPrimary'
              mb='xxs'>
              {t('invitationPreview.task.assignedTo')}
            </Text>
            <Box flexDirection='row' alignItems='center'>
              <Icon
                name='Users'
                variant='s'
                color='textSecondary'
                width={isMobile ? 12 : 18}
                height={isMobile ? 12 : 18}
                marginRight='xxs'
              />
              <Text
                variant={isMobile ? 'metadata1' : 'webBodySecondary'}
                color='textSecondary'>
                {t('invitationPreview.task.peopleWorkingOnThisTask', {
                  count: task.usersCount ?? 0,
                })}
              </Text>
            </Box>
          </Box>
        </Box>

        {/* CTA */}
        <TouchableOpacity
          onPress={() => setSignupModalOpen(true)}
          accessibilityLabel={ctaLabel}>
          <Box
            backgroundColor='greenSecondary'
            alignItems='center'
            justifyContent='center'
            borderRadius={isMobile ? 'm' : 's'}
            style={
              {
                height: theme.spacing.xxl,
                marginBottom: 8,
                ...(isMobile ? { borderRadius: 12 } : {}),
              } as any
            }>
            <Text
              variant={isMobile ? 'bodySecondary' : 'labelSmall'}
              color='white'>
              {ctaLabel}
            </Text>
          </Box>
        </TouchableOpacity>

        {isLinkInvite && (
          <Text
            variant={isMobile ? 'metadata1' : 'webBodySecondary'}
            color='textSecondary'
            textAlign='center'
            mb='l'>
            {t('invitationPreview.task.adminWillReceiveYourRequest')}
          </Text>
        )}

        {/* Already on TaskTag */}
        <Box flexDirection='row' justifyContent='center' mt='l'>
          <Text
            variant={isMobile ? 'metadata1' : 'webBodySecondary'}
            color='textSecondary'>
            {t('invitationPreview.task.alreadyOnTaskTag')}{' '}
          </Text>
          <Text
            variant={isMobile ? 'metadata1' : 'webBodySecondary'}
            color='greenSecondary'
            style={{ fontWeight: '600' } as any}
            onPress={() => (navigation as any).navigate('login')}>
            {t('invitationPreview.task.logIn')}
          </Text>
        </Box>
      </Box>
    );

    if (isMobile) {
      return (
        <Box flex={1} backgroundColor='grey01'>
          {/* Header */}
          <Box
            backgroundColor='white'
            borderBottomWidth={1}
            borderColor='grey02'>
            <Box
              flexDirection='row'
              alignItems='center'
              justifyContent='space-between'
              paddingHorizontal='m'
              paddingVertical='s'
              style={
                { maxWidth: 640, width: '100%', alignSelf: 'center' } as any
              }>
              <Icon name='Logo' width={100} height={30} color='textPrimary' />
              <Text
                variant='webBodySecondary'
                color='greenSecondary'
                style={{ fontWeight: '600' } as any}
                onPress={() => setSignupModalOpen(true)}>
                {t('onBoarding.signUp')}
              </Text>
            </Box>
          </Box>

          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{
              flexGrow: 1,
              alignItems: 'center',
              paddingHorizontal: theme.spacing.m,
              paddingVertical: theme.spacing.xl,
            }}>
            {mainContent}
          </ScrollView>

          {signupModalOpen && (
            <ScopedSignupModal
              invitationType={rawType as 'project' | 'team' | 'task'}
              entityName={task.name}
              projectName={task.projectName ?? undefined}
              projectIcon={task.projectIcon ?? undefined}
              inviterName={invitedByName}
              role={invitationRole}
              email={invitationEmail}
              invitationToken={token}
              entityId={entityId}
              isLinkInvite={isLinkInvite}
              usersCount={task.usersCount ?? null}
              onClose={() => setSignupModalOpen(false)}
              onSuccess={() => setSignupModalOpen(false)}
            />
          )}
        </Box>
      );
    }

    // Desktop
    return (
      <Box flex={1} backgroundColor='grey01'>
        {/* Header */}
        <Box backgroundColor='white' borderBottomWidth={1} borderColor='grey02'>
          <Box
            flexDirection='row'
            alignItems='center'
            justifyContent='space-between'
            paddingHorizontal='m'
            paddingVertical='s'
            style={
              { maxWidth: 640, width: '100%', alignSelf: 'center' } as any
            }>
            <Icon name='Logo' width={120} height={36} color='textPrimary' />
            <Text
              variant='webBodySecondary'
              color='greenSecondary'
              style={{ fontWeight: '600' } as any}
              onPress={() => setSignupModalOpen(true)}>
              {t('onBoarding.signUp')}
            </Text>
          </Box>
        </Box>

        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: 'center',
            alignItems: 'center',
            paddingVertical: theme.spacing.xl,
            paddingHorizontal: theme.spacing.m,
          }}>
          {mainContent}
        </ScrollView>

        {signupModalOpen && (
          <ScopedSignupModal
            invitationType={rawType as 'project' | 'team' | 'task'}
            entityName={task.name}
            projectName={task.projectName ?? undefined}
            projectIcon={task.projectIcon ?? undefined}
            inviterName={invitedByName}
            role={invitationRole}
            email={invitationEmail}
            invitationToken={token}
            entityId={entityId}
            isLinkInvite={isLinkInvite}
            usersCount={task.usersCount ?? null}
            onClose={() => setSignupModalOpen(false)}
            onSuccess={() => setSignupModalOpen(false)}
          />
        )}
      </Box>
    );
  }

  // Project invite layout — full 3-column dashboard preview
  if (!isProjectInvite || !project) {
    return null;
  }

  const buttonHeight = getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT);

  return (
    <Box flex={1} flexDirection='row' style={{ overflow: 'hidden' }}>
      {/* Left Sidebar */}
      <Box
        padding={drawerCollapsed ? 's' : 'l'}
        py='l'
        backgroundColor='grey01'
        width={drawerCollapsed ? 86 : 256}>
        {/* Logo + collapse toggle */}
        <Box
          flexDirection={drawerCollapsed ? 'column' : 'row'}
          justifyContent='space-between'>
          <Box flex={1} alignItems='center' justifyContent='flex-start'>
            <Box alignSelf={drawerCollapsed ? 'center' : 'flex-start'}>
              <Icon
                name={drawerCollapsed ? 'LogoInitials' : 'Logo'}
                width={drawerCollapsed ? 25 : 80}
                height={24}
                color='textPrimary'
              />
            </Box>
          </Box>
          <Box
            flex={1}
            alignItems='center'
            justifyContent='flex-end'
            mt={drawerCollapsed ? 'm' : 'none'}>
            <Box alignSelf={drawerCollapsed ? 'center' : 'flex-end'}>
              <WebIcon
                accessibilityLabel={
                  drawerCollapsed ? 'Expand menu' : 'Collapse menu'
                }
                name={drawerCollapsed ? 'ChevronsRight' : 'ChevronsLeft'}
                variant='l'
                color='grey03'
                onPress={() => setDrawerCollapsed((c) => !c)}
                // Rail bg is grey01 — hover one step deeper.
                hoverBackgroundColor='grey02'
              />
            </Box>
          </Box>
        </Box>

        {/* Nav items */}
        <Box
          flexDirection='column'
          justifyContent='space-between'
          flex={1}
          my='l'>
          <Box flexDirection='column'>
            {/* Projects — active */}
            <Box
              width='100%'
              borderRadius='xs'
              flexDirection='row'
              justifyContent={drawerCollapsed ? 'center' : 'flex-start'}
              padding='m'
              backgroundColor='greenSecondaryMild'
              alignItems='center'
              mb='xxs'>
              <Icon
                name='FolderActive'
                width={20}
                height={20}
                marginRight={drawerCollapsed ? undefined : 'm'}
              />
              {!drawerCollapsed && (
                <Text variant='sidebarNavLabel' color='greenSecondary'>
                  {t('invitationPreview.projects')}
                </Text>
              )}
            </Box>

            {/* Inactive nav rows — real icons, skeleton text */}
            {(
              [
                { iconName: 'TTHashtag', skeletonWidth: 120 },
                { iconName: 'ActivityDot', skeletonWidth: 100 },
                { iconName: 'User', skeletonWidth: 140 },
              ] as const
            ).map(({ iconName, skeletonWidth }) => (
              <Box
                key={iconName}
                width='100%'
                borderRadius='xs'
                flexDirection='row'
                justifyContent={drawerCollapsed ? 'center' : 'flex-start'}
                padding='m'
                alignItems='center'
                mb='xxs'>
                <Icon
                  name={iconName}
                  width={20}
                  height={20}
                  marginRight={drawerCollapsed ? undefined : 'm'}
                  color='grey03'
                />
                {!drawerCollapsed && (
                  <Box
                    height={12}
                    width={skeletonWidth}
                    backgroundColor='grey02'
                    borderRadius='xs'
                  />
                )}
              </Box>
            ))}
          </Box>

          {/* Bottom section — HelpCircle + User with skeleton text */}
          <Box flexDirection='column' width='100%'>
            <Box borderTopWidth={1} borderTopColor='grey02' mb='s' />
            {(['HelpCircle', 'User'] as const).map((iconName) => (
              <Box
                key={iconName}
                width='100%'
                borderRadius='xs'
                flexDirection='row'
                justifyContent={drawerCollapsed ? 'center' : 'flex-start'}
                padding='m'
                alignItems='center'
                mb='xxs'>
                <Icon
                  name={iconName}
                  width={20}
                  height={20}
                  marginRight={drawerCollapsed ? undefined : 'm'}
                  color='grey03'
                />
                {!drawerCollapsed && (
                  <Box
                    height={12}
                    width={iconName === 'HelpCircle' ? 80 : 100}
                    backgroundColor='grey02'
                    borderRadius='xs'
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box flex={1} flexDirection='column' style={{ minWidth: 440 }}>
        {/* Header bar */}
        <Box
          flexDirection='column'
          justifyContent='center'
          style={{
            height: 73,
            borderBottomWidth: 1,
            borderBottomColor: theme.colors.grey02,
          }}>
          <Box height={17} />
          <Box
            flexDirection='row'
            alignItems='center'
            px='s'
            height={buttonHeight}>
            <Text
              variant='heading2'
              numberOfLines={1}
              style={{ maxWidth: 340 }}>
              {project.name}
            </Text>
            <Box flex={1} />
            {/* Search icon */}
            <Box
              alignItems='center'
              justifyContent='center'
              style={{ height: 32, width: 32 }}
              borderRadius='xs'
              paddingHorizontal='xs'
              paddingVertical='xs'
              mr='xs'>
              <Icon color='grey04' variant='l' name='Search' />
            </Box>
            {/* New Task button — disabled appearance */}
            <Box
              mr='l'
              width={122}
              height={buttonHeight}
              flexDirection='row'
              borderWidth={1}
              backgroundColor='grey02'
              borderColor='grey02'
              borderRadius='m'
              alignItems='center'
              px='s'
              ml='xs'>
              <Box
                width={16}
                height={16}
                marginRight='xxs'
                alignItems='center'
                justifyContent='center'>
                <Icon variant='m' name='Plus' color='grey06' />
              </Box>
              <Box
                alignItems='center'
                justifyContent='center'
                style={{ flex: 1 }}>
                <Text variant='labelSmall' color='grey06'>
                  {t('invitationPreview.newTask')}
                </Text>
              </Box>
            </Box>
          </Box>
          {project.address ? (
            <Text pl='s' variant='metadata' color='grey05'>
              {project.address}
            </Text>
          ) : (
            <Box height={17} />
          )}
        </Box>

        {/* Description card */}
        {!!project.description && (
          <Box
            backgroundColor='grey01'
            mx='l'
            mt='m'
            mb='m'
            p='m'
            borderRadius='s'>
            <Text variant='labelEmphasized' color='textPrimary' mb='xxs'>
              {t('invitationPreview.description')}
            </Text>
            <Text
              accessibilityLabel={t('invitationPreview.description')}
              variant='webBodySecondary'
              color='textSecondary'>
              {project.description}
            </Text>
          </Box>
        )}

        {/* Tab bar — disabled */}
        <Box marginVertical='s'>
          <Box
            flexDirection='row'
            alignItems='center'
            justifyContent='space-between'
            borderBottomWidth={1}
            borderBottomColor='grey02'>
            <Box flexDirection='row' alignItems='center' marginHorizontal='s'>
              {TABS.map(({ key, icon }, index) => {
                const isActive = index === 0;
                const color = isActive ? 'greenSecondary' : 'grey03';
                return (
                  <Box
                    key={key}
                    alignItems='center'
                    justifyContent='center'
                    style={
                      {
                        paddingHorizontal: theme.spacing.s,
                        cursor: 'default',
                        height: getScaledSize(42),
                        position: 'relative',
                      } as any
                    }>
                    <Box
                      paddingTop='xs'
                      paddingBottom='m'
                      flexDirection='row'
                      alignItems='center'>
                      <Icon
                        name={icon}
                        color={color}
                        height={16}
                        width={16}
                        marginEnd='xxs'
                      />
                      <Text variant='webSmall' color={color}>
                        {t(`invitationPreview.tabs.${key}`)}
                      </Text>
                      {key === 'members' && project.usersCount > 0 && (
                        <Box
                          marginLeft='xxs'
                          borderRadius='l'
                          width={16}
                          height={16}
                          alignItems='center'
                          alignContent='center'
                          justifyContent='center'
                          backgroundColor={color}>
                          <Text
                            textAlignVertical='center'
                            verticalAlign='middle'
                            variant='bodySecondary2'
                            color='white'>
                            {project.usersCount}
                          </Text>
                        </Box>
                      )}
                    </Box>
                    {isActive && (
                      <Box
                        position='absolute'
                        backgroundColor='greenSecondary'
                        style={
                          {
                            bottom: -1,
                            left: 0,
                            right: 0,
                            height: 2,
                          } as any
                        }
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
            <Box
              flexDirection='row'
              alignItems='center'
              gap='m'
              marginRight='m'>
              {/* Invite button — non-interactive placeholder */}
              <Icon height={24} width={24} name='Link' color='grey03' />
              <TouchableOpacity
                disabled={true}
                activeOpacity={1}
                accessibilityRole='button'
                accessibilityState={{ disabled: true }}
                accessibilityLabel={t('invitationPreview.tabs.invite')}>
                <Box
                  width={122}
                  paddingVertical='xs'
                  borderWidth={1}
                  borderColor='grey02'
                  borderRadius='l'
                  backgroundColor='white'
                  flexDirection='row'
                  alignItems='center'
                  justifyContent='center'
                  paddingHorizontal='m'>
                  <Box marginRight='xs'>
                    <Icon name='UserPlus' color='grey04' variant='s' />
                  </Box>
                  <Text variant='bodyTaskName' color='grey04'>
                    {t('invitationPreview.tabs.invite')}
                  </Text>
                </Box>
              </TouchableOpacity>
            </Box>
          </Box>
        </Box>

        {/* CTA */}
        <Box flex={1} alignItems='center' justifyContent='center' p='xl'>
          <Icon
            name={isLinkInvite ? 'Share' : 'UserPlus'}
            color='grey05'
            width={40}
            height={40}
          />
          <Text
            variant='heading2'
            color='textPrimary'
            textAlign='center'
            mt='m'
            mb='l'>
            {isLinkInvite ? (
              t('invitationPreview.youveBeenSharedAProject')
            ) : (
              <>
                {`${t('invitationPreview.youveBeenInvitedBy')}${
                  invitedByName ? ' ' : ''
                }`}
                {!!invitedByName && (
                  <Text variant='heading2' color='textPrimary'>
                    {invitedByName}
                  </Text>
                )}
              </>
            )}
          </Text>
          <TouchableOpacity
            onPress={() => setSignupModalOpen(true)}
            accessibilityLabel={
              isLinkInvite
                ? t('invitationPreview.requestToJoin')
                : t('invitationPreview.joinThisProject')
            }>
            <Box
              backgroundColor='textPrimary'
              alignItems='center'
              justifyContent='center'
              style={{
                borderRadius: theme.borderRadii.l,
                height: theme.spacing.xxl,
                minWidth: 220,
                paddingHorizontal: theme.spacing.xl,
              }}>
              <Text variant='labelSmall' color='white'>
                {isLinkInvite
                  ? t('invitationPreview.requestToJoin')
                  : t('invitationPreview.joinThisProject')}
              </Text>
            </Box>
          </TouchableOpacity>
        </Box>
      </Box>

      {/* Right Sidebar — Chat panel */}
      <Box
        width={480}
        backgroundColor='white'
        borderLeftWidth={1}
        borderLeftColor='grey02'
        px='m'
        py='l'>
        <Text variant='labelLarge' color='textPrimary' mb='m'>
          {t('invitationPreview.chat')}
        </Text>
        {/* Skeleton chat entry */}
        <Box flexDirection='row' alignItems='flex-start'>
          {/* Orange avatar with user icon */}
          <Box
            width={40}
            height={40}
            backgroundColor='orange'
            alignItems='center'
            justifyContent='center'
            marginRight='s'
            style={{ borderRadius: theme.borderRadii.xl, flexShrink: 0 }}>
            <Icon name='User' variant='s' color='white' />
          </Box>
          <Box flex={1}>
            <Box
              height={10}
              backgroundColor='grey02'
              borderRadius='xs'
              mb='s'
              style={{ width: `30%` }}
            />
            <Box justifyContent='space-between' flexDirection='row'>
              <Box
                height={10}
                backgroundColor='grey02'
                borderRadius='xs'
                mb='s'
                style={{ width: `70%` }}
              />
              <Box
                height={10}
                backgroundColor='grey02'
                borderRadius='xs'
                mb='s'
                style={{ width: `10%` }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {signupModalOpen && (
        <ScopedSignupModal
          invitationType={rawType as 'project' | 'team' | 'task'}
          entityName={project.name}
          entityAddress={project.address ?? undefined}
          inviterName={invitedByName}
          role={invitationRole}
          email={invitationEmail}
          invitationToken={token}
          entityId={entityId}
          isLinkInvite={isLinkInvite}
          onClose={() => setSignupModalOpen(false)}
          onSuccess={() => setSignupModalOpen(false)}
        />
      )}
    </Box>
  );
};
